import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckSquare, Clock, User as UserIcon, Calendar, Edit, Trash2, List, MoreHorizontal, MoreVertical, Search, Filter, ArrowUpDown, LayoutGrid, BarChart3, ArrowLeft, PlayCircle, Target, DollarSign, Users, Activity, Edit3, MessageSquare, FileText, TrendingUp, X, AlertTriangle, Award } from 'lucide-react';
import { Task, User, NewTask, Project } from '../../types';
import { useAuth, useModal } from '../../hooks';
import api from '../../services/api';
import { TaskForm } from './TaskForm';
import KanbanBoard from './KanbanBoard';
import GanttChart from './GanttChart';
import DocView from './DocView';
import JiraBadge from '../jira/JiraBadge';
import { PageHeader, Button } from '../common';

type SidePanelView = 'overview' | 'team' | 'time' | 'activity' | null;

// Task Management Component
const TaskManagement: React.FC<{
  projectId: number;
  projectName: string;
  onSelectTask?: (task: Task) => void;
  onBack?: () => void;
  onEditProject?: () => void;
}> = ({ projectId, projectName, onSelectTask, onBack, onEditProject }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const { isOpen: showTaskForm, openModal: openTaskForm, closeModal: closeTaskForm } = useModal();
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sidePanelView, setSidePanelView] = useState<SidePanelView>(null);
  const [sortColumn, setSortColumn] = useState<'assignee' | 'priority' | 'status' | 'time' | 'due'>(() => {
    const savedSortColumn = localStorage.getItem('taskSortColumn');
    return (savedSortColumn as 'assignee' | 'priority' | 'status' | 'time' | 'due') || 'priority';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const savedSortDirection = localStorage.getItem('taskSortDirection');
    return (savedSortDirection as 'asc' | 'desc') || 'desc';
  });
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'gantt' | 'doc'>(() => {
    const savedViewMode = localStorage.getItem('taskViewMode');
    return (savedViewMode === 'list' || savedViewMode === 'kanban' || savedViewMode === 'gantt' || savedViewMode === 'doc')
      ? savedViewMode
      : 'list';
  });
  const [compactMode, setCompactMode] = useState<boolean>(() => {
    const savedCompactMode = localStorage.getItem('taskCompactMode');
    return savedCompactMode === 'true';
  });
  const { token, user } = useAuth();

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);

      const [tasksResponse, usersResponse, projectResponse] = await Promise.all([
        api.get<any>(`/api/projects/${projectId}/tasks`, token || undefined),
        api.get<any>('/api/users', token || undefined),
        api.get<any>(`/api/projects/${projectId}`, token || undefined)
      ]);

      const tasksArray = Array.isArray(tasksResponse) ? tasksResponse :
                         (tasksResponse && Array.isArray(tasksResponse.tasks)) ? tasksResponse.tasks : [];
      setTasks(tasksArray);

      const usersArray = Array.isArray(usersResponse) ? usersResponse :
                         (usersResponse && Array.isArray(usersResponse.users)) ? usersResponse.users : [];
      setUsers(usersArray);

      setProject(projectResponse || null);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks([]);
      setUsers([]);
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreateTask = async (taskData: NewTask) => {
    try {
      setTaskLoading(true);
      
      const backendData = {
        title: taskData.title,
        description: taskData.description,
        status: taskData.status,
        priority: taskData.priority,
        assigned_to: taskData.assignedTo || null,
        due_date: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
        estimated_hours: (taskData.estimatedHours || 0) > 0 ? taskData.estimatedHours : null
      };
      
      await api.post<any>(`/api/projects/${projectId}/tasks`, backendData, token || undefined);
      closeTaskForm();
      fetchTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      alert('Failed to create task. Please try again.');
    } finally {
      setTaskLoading(false);
    }
  };

  const handleUpdateTask = async (taskData: NewTask) => {
    if (!editingTask) return;
    
    try {
      setTaskLoading(true);
      
      const backendData = {
        title: taskData.title,
        description: taskData.description,
        status: taskData.status,
        priority: taskData.priority,
        assigned_to: taskData.assignedTo || null,
        due_date: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
        estimated_hours: (taskData.estimatedHours || 0) > 0 ? taskData.estimatedHours : null,
      };
      
      await api.put<any>(`/api/projects/tasks/${editingTask.id}`, backendData, token || undefined);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      alert('Failed to update task. Please try again.');
    } finally {
      setTaskLoading(false);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await api.delete<any>(`/api/projects/tasks/${taskId}`, token || undefined);
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleStatusChange = async (task: Task, newStatus: string) => {
    try {
      await api.put<any>(`/api/projects/tasks/${task.id}`, { status: newStatus }, token || undefined);
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task status:', error);
      alert('Failed to update task status. Please try again.');
    }
  };

  const handleTaskUpdate = async (taskId: number, updates: Partial<Task>) => {
    try {
      await api.put<any>(`/api/projects/tasks/${taskId}`, updates, token || undefined);

      // Update local state instead of refetching all tasks for better performance
      setTasks(currentTasks =>
        currentTasks.map(task =>
          task.id === taskId ? { ...task, ...updates } : task
        )
      );
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error;
    }
  };

  // Helper function to save view mode to localStorage and update state
  const handleViewModeChange = (newViewMode: 'list' | 'kanban' | 'gantt' | 'doc') => {
    setViewMode(newViewMode);
    localStorage.setItem('taskViewMode', newViewMode);
  };

  const handleCompactModeChange = (enabled: boolean) => {
    setCompactMode(enabled);
    localStorage.setItem('taskCompactMode', enabled.toString());
  };

  const toggleSidePanel = (view: SidePanelView) => {
    if (sidePanelView === view) {
      setSidePanelView(null);
    } else {
      setSidePanelView(view);
    }
  };

  const handleSort = (column: 'assignee' | 'priority' | 'status' | 'time' | 'due') => {
    if (sortColumn === column) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      localStorage.setItem('taskSortDirection', newDirection);
    } else {
      setSortColumn(column);
      setSortDirection('desc');
      localStorage.setItem('taskSortColumn', column);
      localStorage.setItem('taskSortDirection', 'desc');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'review': return 'bg-purple-100 text-purple-800';
      case 'todo': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'review': return 'Review';
      case 'todo': return 'To Do';
      default: return status;
    }
  };

  const generateTaskId = (taskId: number) => {
    return `TSK-${String(taskId).padStart(5, '0')}`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateDaysLeft = () => {
    if (!project?.dueDate) return 0;
    const due = new Date(project.dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateProgress = () => {
    if (tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === 'completed').length;
    return Math.round((completed / tasks.length) * 100);
  };

  const getStatusStyles = (status: string) => {
    switch(status) {
      case 'active': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'planning': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'on-hold': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'cancelled': return 'bg-gray-50 text-gray-600 border-gray-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const filteredTasks = (Array.isArray(tasks) ? tasks : []).filter(task => {
    const matchesSearch = searchTerm === '' ||
                         task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesAssignee = assigneeFilter === 'all' ||
                           (assigneeFilter === 'unassigned' && !task.assignedTo) ||
                           task.assignedTo?.toString() === assigneeFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
  }).sort((a, b) => {
    let aVal: any, bVal: any;

    switch (sortColumn) {
      case 'assignee':
        aVal = a.assignedUser ? `${a.assignedUser.first_name} ${a.assignedUser.last_name}` : '';
        bVal = b.assignedUser ? `${b.assignedUser.first_name} ${b.assignedUser.last_name}` : '';
        break;
      case 'priority':
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        aVal = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        bVal = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
        break;
      case 'status':
        const statusOrder = { todo: 1, in_progress: 2, review: 3, completed: 4 };
        aVal = statusOrder[a.status as keyof typeof statusOrder] || 0;
        bVal = statusOrder[b.status as keyof typeof statusOrder] || 0;
        break;
      case 'time':
        aVal = a.estimatedHours || 0;
        bVal = b.estimatedHours || 0;
        break;
      case 'due':
        aVal = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        bVal = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const taskStats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    review: tasks.filter(t => t.status === 'review').length,
    completed: tasks.filter(t => t.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading tasks...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-lg text-gray-600">Project not found</div>
      </div>
    );
  }

  const progress = calculateProgress();
  const daysLeft = calculateDaysLeft();

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={project.name}
        breadcrumb={
          <button
            onClick={onBack || (() => window.history.back())}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </button>
        }
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusStyles(project.status)}`}>
                <PlayCircle className="w-3 h-3" />
                {project.status.replace('_', ' ')}
              </span>
              <span>•</span>
              <span>Client: {project.client?.name || 'N/A'}</span>
              {project.dueDate && (
                <>
                  <span>•</span>
                  <span className={daysLeft < 7 ? 'text-amber-600 font-medium' : ''}>
                    {daysLeft} days left
                  </span>
                </>
              )}
            </div>
            <div className="w-px h-6 bg-gray-300"></div>
            <Button
              variant="secondary"
              size="sm"
              icon={<Edit3 className="w-3.5 h-3.5" />}
              onClick={onEditProject}
            >
              Edit Project
            </Button>
          </div>
        }
      />

    <div className="px-6 py-6">
      <div className={`flex gap-6 transition-all duration-300 ${sidePanelView ? '' : ''}`}>
        {/* Main Content */}
        <div className="flex-1">
          {/* Project Description */}
          {project.description && (
            <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
              <p className="text-sm text-gray-700 leading-relaxed">{project.description}</p>
            </div>
          )}

          {/* Project Metrics Bar */}
          <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
        <div className="grid grid-cols-6 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Progress</p>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold text-gray-900">{progress}%</p>
                <div className="w-24 bg-gray-200 rounded-full h-1.5">
                  <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Budget Used</p>
              <p className="text-lg font-semibold text-gray-900">
                ${((project.actualHours || 0) * (project.hourlyRate || 0) / 1000).toFixed(1)}K / ${((project.budget || 0) / 1000).toFixed(0)}K
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Hours Logged</p>
              <p className="text-lg font-semibold text-gray-900">{project.actualHours || 0}h / {project.estimatedHours || 0}h</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Tasks</p>
              <p className="text-lg font-semibold text-gray-900">
                {tasks.filter(t => t.status === 'completed').length} / {tasks.length}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Team</p>
              <p className="text-lg font-semibold text-gray-900">{users.length} members</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Efficiency</p>
              <p className="text-lg font-semibold text-gray-900">92%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Task Controls */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="review">Review</option>
              <option value="completed">Completed</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg"
            >
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <div className="flex items-center gap-1 border border-gray-300 rounded-lg">
              <button
                onClick={() => handleViewModeChange('list')}
                className={`px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
              >
                List
              </button>
              <button
                onClick={() => handleViewModeChange('kanban')}
                className={`px-3 py-2 text-sm ${viewMode === 'kanban' ? 'bg-gray-100' : ''}`}
              >
                Kanban
              </button>
              <button
                onClick={() => handleViewModeChange('gantt')}
                className={`px-3 py-2 text-sm ${viewMode === 'gantt' ? 'bg-gray-100' : ''}`}
              >
                Gantt
              </button>
              <button
                onClick={() => handleViewModeChange('doc')}
                className={`px-3 py-2 text-sm ${viewMode === 'doc' ? 'bg-gray-100' : ''}`}
              >
                Doc
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="h-8 w-px bg-gray-300"></div>
            <button
              onClick={() => toggleSidePanel('overview')}
              className={`p-2 rounded-lg transition-all ${
                sidePanelView === 'overview'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Overview"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={() => toggleSidePanel('team')}
              className={`p-2 rounded-lg transition-all ${
                sidePanelView === 'team'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Team"
            >
              <Users className="w-4 h-4" />
            </button>
            <button
              onClick={() => toggleSidePanel('time')}
              className={`p-2 rounded-lg transition-all ${
                sidePanelView === 'time'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Time"
            >
              <Clock className="w-4 h-4" />
            </button>
            <button
              onClick={() => toggleSidePanel('activity')}
              className={`p-2 rounded-lg transition-all ${
                sidePanelView === 'activity'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Activity"
            >
              <Activity className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={openTaskForm}
            className="ml-3 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Task
          </button>
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'kanban' && (
        <div className={compactMode ? "p-2" : "p-3"}>
          <KanbanBoard
            tasks={filteredTasks}
            users={users}
            onTaskUpdate={handleTaskUpdate}
            onSelectTask={onSelectTask}
            onCreateTask={openTaskForm}
            compactMode={compactMode}
          />
        </div>
      )}

      {viewMode === 'gantt' && (
        <div className="p-3">
          <GanttChart
            tasks={filteredTasks}
            users={users}
            onSelectTask={onSelectTask}
            onTaskUpdate={handleTaskUpdate}
          />
        </div>
      )}

      {viewMode === 'doc' && (
        <div className="p-3">
          <DocView
            tasks={filteredTasks}
            users={users}
            project={project}
            onSelectTask={onSelectTask}
          />
        </div>
      )}

      {/* Task Table */}
      {viewMode === 'list' && (
        filteredTasks.length > 0 ? (
          <div className="flex-1 overflow-auto bg-white">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-6 text-xs font-medium text-gray-600 uppercase">Task</th>
                  <th className="text-left py-2 px-4">
                    <button
                      onClick={() => handleSort('assignee')}
                      className={`flex items-center gap-1 text-xs font-medium uppercase hover:text-gray-900 transition-colors ${
                        sortColumn === 'assignee' ? 'text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      Assignee
                      {sortColumn === 'assignee' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left py-2 px-4">
                    <button
                      onClick={() => handleSort('priority')}
                      className={`flex items-center gap-1 text-xs font-medium uppercase hover:text-gray-900 transition-colors ${
                        sortColumn === 'priority' ? 'text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      Priority
                      {sortColumn === 'priority' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left py-2 px-4">
                    <button
                      onClick={() => handleSort('status')}
                      className={`flex items-center gap-1 text-xs font-medium uppercase hover:text-gray-900 transition-colors ${
                        sortColumn === 'status' ? 'text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      Status
                      {sortColumn === 'status' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left py-2 px-4">
                    <button
                      onClick={() => handleSort('time')}
                      className={`flex items-center gap-1 text-xs font-medium uppercase hover:text-gray-900 transition-colors ${
                        sortColumn === 'time' ? 'text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      Time
                      {sortColumn === 'time' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left py-2 px-4">
                    <button
                      onClick={() => handleSort('due')}
                      className={`flex items-center gap-1 text-xs font-medium uppercase hover:text-gray-900 transition-colors ${
                        sortColumn === 'due' ? 'text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      Due
                      {sortColumn === 'due' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left py-2 px-4 text-xs font-medium text-gray-600 uppercase">Comments</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => {
                  const timeTracked = 0;
                  const estimate = task.estimatedHours || 0;
                  const timeProgress = estimate > 0 ? Math.min((timeTracked / estimate) * 100, 100) : 0;

                  return (
                    <tr
                      key={task.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer group"
                      onClick={() => onSelectTask?.(task)}
                    >
                      <td className="py-3 px-6">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{task.title}</p>
                            {(task as any).jira_issue_key && (
                              <JiraBadge
                                issueKey={(task as any).jira_issue_key}
                                className="flex-shrink-0"
                              />
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{generateTaskId(task.id)} • {task.description?.substring(0, 40) || 'No description'}...</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-indigo-700">
                              {task.assignedUser ? `${task.assignedUser.first_name?.[0] || ''}${task.assignedUser.last_name?.[0] || ''}` : '?'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {task.priority ? (
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(task.status)}`}>
                          {getStatusLabel(task.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <p className="text-gray-900">{timeTracked}h / {estimate}h</p>
                          <div className="w-16 bg-gray-200 rounded-full h-1 mt-1">
                            <div
                              className={`h-1 rounded-full ${timeTracked > estimate ? 'bg-red-500' : 'bg-green-500'}`}
                              style={{ width: `${timeProgress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-600">
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <MessageSquare className="w-3.5 h-3.5" />
                          {task.comment_count || 0}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTask(task);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <CheckSquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first task.
            </p>
            <div className="mt-6">
              <button
                onClick={openTaskForm}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Task
              </button>
            </div>
          </div>
        )
      )}

        {/* Add Task Form */}
        {showTaskForm && (
          <TaskForm
            onClose={() => closeTaskForm()}
            onSubmit={handleCreateTask}
            loading={taskLoading}
            projectId={projectId}
            users={users}
          />
        )}

        {/* Edit Task Form */}
        {editingTask && (
          <TaskForm
            task={editingTask}
            onClose={() => setEditingTask(null)}
            onSubmit={handleUpdateTask}
            loading={taskLoading}
            projectId={projectId}
            users={users}
          />
        )}
        </div>
      </div>

      {/* Side Panel */}
      {sidePanelView && (
        <div className="w-90 bg-white border-l border-gray-200 shadow-lg overflow-y-auto flex-shrink-0 animate-slideInRight">
          {/* Panel Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-gray-900 capitalize">{sidePanelView}</h2>
            <button
              onClick={() => setSidePanelView(null)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="p-4">
            {/* Overview Panel */}
            {sidePanelView === 'overview' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Project Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Status</span>
                      <span className="font-medium text-gray-900 capitalize">{project?.status || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Budget</span>
                      <span className="font-medium text-gray-900">${(project?.budget || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Hours Est.</span>
                      <span className="font-medium text-gray-900">{project?.estimatedHours || 0}h</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Due Date</span>
                      <span className="font-medium text-gray-900">
                        {project?.dueDate ? new Date(project.dueDate).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Key Metrics</h3>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Completion</span>
                        <span className="font-medium text-gray-900">{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Budget Used</span>
                        <span className="font-medium text-gray-900">
                          {project?.budget ? Math.round(((project.actualHours || 0) * (project.hourlyRate || 0) / project.budget) * 100) : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${project?.budget ? Math.min(((project.actualHours || 0) * (project.hourlyRate || 0) / project.budget) * 100, 100) : 0}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Task Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <span className="text-sm text-gray-700">To Do</span>
                      <span className="text-sm font-semibold text-blue-600">{tasks.filter(t => t.status === 'todo').length}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-amber-50 rounded">
                      <span className="text-sm text-gray-700">In Progress</span>
                      <span className="text-sm font-semibold text-amber-600">{tasks.filter(t => t.status === 'in_progress').length}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                      <span className="text-sm text-gray-700">Review</span>
                      <span className="text-sm font-semibold text-purple-600">{tasks.filter(t => t.status === 'review').length}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-emerald-50 rounded">
                      <span className="text-sm text-gray-700">Completed</span>
                      <span className="text-sm font-semibold text-emerald-600">{tasks.filter(t => t.status === 'completed').length}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Risk Assessment</h3>
                  <div className="space-y-2">
                    {daysLeft < 7 && daysLeft > 0 && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-900">Due Date Approaching</p>
                          <p className="text-xs text-amber-700">Only {daysLeft} days remaining</p>
                        </div>
                      </div>
                    )}
                    {progress < 50 && daysLeft < 14 && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900">Behind Schedule</p>
                          <p className="text-xs text-red-700">Progress is slower than expected</p>
                        </div>
                      </div>
                    )}
                    {progress >= 80 && (
                      <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <Award className="w-4 h-4 text-emerald-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-emerald-900">On Track</p>
                          <p className="text-xs text-emerald-700">Great progress!</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Team Panel */}
            {sidePanelView === 'team' && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Team Performance</h3>
                {users.slice(0, 5).map(member => {
                  const memberTasks = tasks.filter(t => t.assignedTo === member.id);
                  const completedTasks = memberTasks.filter(t => t.status === 'completed').length;
                  const efficiency = memberTasks.length > 0 ? Math.round((completedTasks / memberTasks.length) * 100) : 0;

                  return (
                    <div key={member.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-700">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{member.first_name} {member.last_name}</p>
                          <p className="text-xs text-gray-500">{member.roles?.[0]?.name || 'Team Member'}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Tasks: {memberTasks.length}</span>
                          <span className="text-gray-600">Hours: {memberTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0)}h</span>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600">Efficiency</span>
                            <span className="font-medium text-gray-900">{efficiency}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${efficiency}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Time Panel */}
            {sidePanelView === 'time' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Time Tracking</h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-center mb-3">
                      <p className="text-3xl font-bold text-gray-900">{project?.actualHours || 0}h</p>
                      <p className="text-sm text-gray-600">Total Hours Logged</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Estimated</span>
                        <span className="font-medium text-gray-900">{project?.estimatedHours || 0}h</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Remaining</span>
                        <span className="font-medium text-gray-900">{Math.max((project?.estimatedHours || 0) - (project?.actualHours || 0), 0)}h</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Variance</span>
                        <span className={`font-medium ${(project?.actualHours || 0) > (project?.estimatedHours || 0) ? 'text-red-600' : 'text-emerald-600'}`}>
                          {((project?.actualHours || 0) - (project?.estimatedHours || 0)) > 0 ? '+' : ''}{((project?.actualHours || 0) - (project?.estimatedHours || 0))}h
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Weekly Breakdown</h3>
                  <div className="space-y-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, index) => {
                      const hours = Math.floor(Math.random() * 8) + 1;
                      return (
                        <div key={day} className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 w-8">{day}</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${(hours / 8) * 100}%` }}></div>
                          </div>
                          <span className="text-xs font-medium text-gray-900 w-8">{hours}h</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Activity Panel */}
            {sidePanelView === 'activity' && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Recent Activity</h3>
                <div className="space-y-2">
                  {tasks.slice(0, 8).map((task, index) => (
                    <div key={task.id} className="flex gap-2 pb-2 border-b border-gray-100 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <CheckSquare className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                        <p className="text-xs text-gray-500">Updated {index} hours ago</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
  );
};
export default TaskManagement;